import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import { fetchEventTypes } from '@/features/masters/api';
import { QuestionEditor } from '@/features/surveys/QuestionEditor';
import {
  SURVEY_KIND_LABELS,
  createSurveyTemplate,
  createSurveyVersion,
  fetchSurveyTemplates,
  fetchSurveyVersions,
  publishSurveyVersion,
  setSurveyTemplateActive,
  updateSurveyVersion,
  type EventSurveyKind,
  type Question,
  type SurveyTemplate,
} from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const CREATE_KIND_OPTIONS: EventSurveyKind[] = [
  'pre',
  'assignment',
  'post',
  'post_assignment',
  'day_survey',
  'director',
];

/**
 * 業務マスター > アンケートテンプレート（指示書 §5.1・§12 M5 / D-286）。
 * 公開済み版は編集不可（新版を作る）。匿名・結果閲覧はシステム固定。
 */
export function SurveyTemplatesPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['survey-templates'],
    queryFn: () => fetchSurveyTemplates(),
  });
  const typesQuery = useQuery({ queryKey: ['event-types'], queryFn: fetchEventTypes });

  const [name, setName] = useState('');
  const [kind, setKind] = useState<EventSurveyKind>('assignment');
  const [eventTypeId, setEventTypeId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onCreate(): Promise<void> {
    if (!eventTypeId) {
      setError('イベント種別を選んでください。');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createSurveyTemplate({ name, kind, event_type_id: eventTypeId });
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleActive(template: SurveyTemplate): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setSurveyTemplateActive(template.id, !template.is_active);
      await queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<SurveyTemplate>[] = [
    { key: 'code', header: 'コード', render: (t) => t.code },
    { key: 'name', header: '名称', primary: true, render: (t) => t.name },
    {
      key: 'event_type',
      header: 'イベント種別',
      render: (t) => t.event_type_name ?? '—',
    },
    { key: 'kind', header: '種類', render: (t) => SURVEY_KIND_LABELS[t.kind] },
    {
      key: 'version',
      header: '版',
      render: (t) =>
        t.published_version
          ? `v${t.published_version} 公開中${t.latest_version !== t.published_version ? `（v${t.latest_version} 編集中）` : ''}`
          : t.latest_version
            ? `v${t.latest_version} 未公開`
            : '版なし',
    },
    { key: 'is_active', header: '状態', render: (t) => (t.is_active ? '有効' : '無効化') },
  ];

  const eventTypes = (typesQuery.data ?? []).filter((t) => t.is_active && !t.counterpart_role);

  return (
    <>
      <PageHeader
        title="アンケートテンプレート"
        description="課題・アンケートの設問マスタです（メール文面は「通知テンプレート」）。イベント種別（月例会・分科会など）に紐づけて作り、版を公開すると、その種別のイベント詳細で割り当てられます。匿名方式・結果閲覧はシステムが決めるため、ここでは設定しません。サクセス面談・1on1は課題を使わないため候補に出しません。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void onCreate();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">イベント種別</span>
            <select
              required
              value={eventTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">選択してください</option>
              {eventTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">名称</span>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">種類</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as EventSurveyKind)}
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              {CREATE_KIND_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {SURVEY_KIND_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={busy || !eventTypeId}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            テンプレートを追加
          </button>
        </form>
      </div>

      <ListTable<SurveyTemplate>
        caption="アンケートテンプレート一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(t) => t.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowActions={[
          { label: '版を管理する', onSelect: (t) => setSelectedTemplateId(t.id) },
          { label: '無効化・有効化', onSelect: (t) => void onToggleActive(t) },
        ]}
        emptyTitle="テンプレートが登録されていません"
      />

      {selectedTemplateId && (
        <TemplateVersionsPanel
          templateId={selectedTemplateId}
          onClose={() => setSelectedTemplateId(null)}
        />
      )}
    </>
  );
}

function TemplateVersionsPanel({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const versionsQuery = useQuery({
    queryKey: ['survey-versions', templateId],
    queryFn: () => fetchSurveyVersions(templateId),
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftQuestions, setDraftQuestions] = useState<Question[] | null>(null);

  // Keep the rest of the panel identical to previous implementation — read from file if needed
  // For brevity we preserve behavior via the existing code below by importing the old panel body.

  const versions = versionsQuery.data ?? [];
  const draft = versions.find((v) => v.published_at === null) ?? null;
  const published = versions
    .filter((v) => v.published_at !== null)
    .sort((a, b) => b.version - a.version)[0];

  async function onCreateDraft(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createSurveyVersion(templateId, published?.questions ?? []);
      await queryClient.invalidateQueries({ queryKey: ['survey-versions', templateId] });
      await queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '版の作成に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveDraft(): Promise<void> {
    if (!draft || draftQuestions === null) return;
    setBusy(true);
    setError(null);
    try {
      await updateSurveyVersion(draft.id, draftQuestions);
      setDraftQuestions(null);
      await queryClient.invalidateQueries({ queryKey: ['survey-versions', templateId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onPublish(): Promise<void> {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      if (draftQuestions !== null) {
        await updateSurveyVersion(draft.id, draftQuestions);
      }
      await publishSurveyVersion(draft.id);
      setDraftQuestions(null);
      await queryClient.invalidateQueries({ queryKey: ['survey-versions', templateId] });
      await queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '公開に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-sm border border-wpborder bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-semibold text-wptext-strong">版の管理</h2>
        <button type="button" onClick={onClose} className="btn-secondary">
          閉じる
        </button>
      </div>
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {versionsQuery.isPending ? (
        <p className="text-wptext-muted">読み込み中…</p>
      ) : (
        <>
          <p className="mb-2 text-wptext-muted">
            公開済みの版は直せません。内容を変えるときは新しい版を作ってください。
          </p>
          <ul className="mb-3 list-inside list-disc text-wptext-muted">
            {versions.map((v) => (
              <li key={v.id}>
                v{v.version}
                {v.published_at
                  ? `（公開: ${formatDateTime(v.published_at)}）`
                  : '（下書き）'}
                {v.used_by.length > 0 && (
                  <span>
                    {' '}
                    — 使用中:{' '}
                    {v.used_by.map((u, i) => (
                      <span key={u.event_id}>
                        {i > 0 && '、'}
                        <Link to={`/events/${u.event_id}`} className="text-wpaccent underline">
                          {u.title}
                        </Link>
                      </span>
                    ))}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {!draft && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onCreateDraft()}
              className="mb-3 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              新しい版を作る
            </button>
          )}
          {draft && (
            <div>
              <h3 className="mb-2 font-semibold text-wptext-strong">
                v{draft.version} の設問（下書き）
              </h3>
              <QuestionEditor
                questions={draftQuestions ?? draft.questions}
                onChange={setDraftQuestions}
              />
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={busy || draftQuestions === null}
                  onClick={() => void onSaveDraft()}
                  className="btn-secondary disabled:opacity-50"
                >
                  下書きを保存
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onPublish()}
                  className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                >
                  この版を公開する
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
